// ⚠️ PR #74 - xime: Este archivo fue restaurado al estado pre-pull (commit 4999deb).
// Cambios que hizo el PR (pendientes de revisar/integrar cuando se necesite):
// - type valor por defecto cambió de 'url' → 'urlBi'
// - urlBi: quitó Validators.required (ahora es dinámico según tipo seleccionado)
// - Nuevo método setupDynamicValidators(): valida urlBi solo cuando type === 'urlBi'
// - Nuevo método addRoleFromInput(): agrega rol al presionar Enter en el input
// - Nuevo método markFormChanged(): centraliza for_save=true + markAsDirty
// - addRole(): normaliza email a minúsculas, llama markFormChanged()
// - removeRole(): llama markFormChanged()
// - loadBoardById(): client_id coercionado a Number, fallbacks en urlBi y type
import { ChangeDetectorRef, Component, signal, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, map, Observable, shareReplay, startWith, Subject } from 'rxjs';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { MatNativeDateModule, MatOption } from '@angular/material/core';
import { MatLabel } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';

import { Clients } from '../models/clients';
import { Dashboards } from '../models/dashboards';
import { Users } from '../models/user';
import { ClientsService } from '../services/clients.service';
import { DashboardsService } from '../services/dashboards.service';
import { AuthService } from '../services/auth.service';
import { MainHeaderComponent } from '../components/shared/main-header/main-header.component';
import { SendRecognitionBottomsheetComponent } from '../components/shared/send-recognition-bottomsheet/send-recognition-bottomsheet.component';
import { ImportFileUploadComponent } from '../components/shared/import-file-upload/import-file-upload.component';
import { DataTableComponent } from '../components/shared/data-table/data-table.component';
import { SafePipe } from '../pipes/safe.pipe';
import { DashboardService as MetabaseEmbedService } from '../components/boards/board.service';
import { getUrlTemplateKeys } from '../components/boards/url-template';
import {
  DashboardRoleEditDialogComponent,
  DashboardRoleParameter,
  DashboardRoleRow
} from './dashboard-role-edit-dialog.component';
import { MatAccordion, MatExpansionModule } from '@angular/material/expansion';
import { MatChipsModule } from '@angular/material/chips';
import { DashboardParameterObjectsModalComponent } from './dashboard-parameter-objects-modal.component';
import { MetabaseSelectedDialogComponent } from '../components/shared/metabase-selected-dialog/metabase-selected-dialog.component';

interface DashboardRoleFormValue {
  uid: string;
  role: string;
  email: string;
  name: string;
}

interface DashboardParametersByUser {
  uid: string;
  parameters: DashboardRoleParameter[];
}

interface DashboardParameterObjectItem {
  label: string;
  type: string;
}

interface DashboardParameterSummaryItem {
  key: string;
  label: string;
}

interface DashboardParameterSummaryVm {
  has_parameters: boolean;
  parameter_object_entries: DashboardParameterSummaryItem[];
}

@Component({
  selector: 'app-dashboards',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    MatOption,
    MatLabel,
    MatTooltipModule,
    MainHeaderComponent,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatNativeDateModule,
    MatDatepickerModule,
    MatCheckboxModule,
    MatDialogModule,
    DataTableComponent,
    MatExpansionModule,
    MatChipsModule,
    SafePipe,
  ],
  templateUrl: './dashboards.component.html',
  styleUrl: './dashboards.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class DashboardsComponent {
  accordion = viewChild.required(MatAccordion);
  readonly panelOpenState = signal(false);
  oculto = false;
  boardUid = '';
  boardData: any;
  showConfig = false;
  clients: Clients[] = [];
  roleSearchCtrl = new FormControl<Users | string | null>('');
  filteredUsers: Observable<Users[]> = new Observable();
  users: Users[] = [];
  private readonly users_subject = new BehaviorSubject<Users[]>([]);
  readonly users$ = this.users_subject.asObservable();
  current_user_data: any = null;
  pickers: any[] = [];
  roleRows: DashboardRoleRow[] = [];
  roleRows$: Observable<DashboardRoleRow[]> = new Observable();
  roleEmailsSummary$: Observable<string> = new Observable();
  rolesPanelExpanded = true;
  showPreview = false;
  previewLoading = false;
  previewError: string | null = null;
  metabasePreviewUrl: string | null = null;
  selectedFile: string | null = null;
  selectedFileName = '';
  readonly parameters_objects_subject = new BehaviorSubject<Record<string, DashboardParameterObjectItem>>({});
  readonly parameters_objects$ = this.parameters_objects_subject.asObservable();
  readonly parameters_summary_vm$ = this.parameters_objects$.pipe(
    map((parameters_objects) => this.buildParametersSummary(parameters_objects)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private default_boards: Partial<Dashboards> = {
    client_id: 1,
    dashboard_id: ' ',
    name: ' ',
    roles: {},
    parameters: {},
    parameters_objects: {},
    url_image: ' ',
    urlBi: ' ',
    type: ''
  };

  private board_subject = new BehaviorSubject<Dashboards | Partial<Dashboards> | undefined>(this.default_boards);
  board$: Observable<Dashboards | Partial<Dashboards> | undefined> = this.board_subject.asObservable();

  titleheader = 'Dashboard';
  creating = true;
  for_save = false;

  formConfig = [
    { controlName: 'client_id', label: 'Cliente', type: 'select', width: '100%', margin: '' },
    { controlName: 'name', label: 'Nombre del tablero', type: 'text', width: '100%', margin: '' },
    { controlName: 'type', label: 'Tipo de tablero', type: 'select', width: '100%', margin: '' },
    { controlName: 'urlBi', label: 'URL pública web', type: 'text', width: '100%', margin: '' },
    { controlName: 'urlImage', label: 'URL Imagen', type: 'text', width: '100%', margin: '' }
  ];

  columns: any[] = [
    { field: 'name', header: 'Usuario', filter: true, orderby: true },
    { field: 'email', header: 'Correo', filter: true, orderby: true },
  ];

  types: any[] = [
    { value: 'metabase', viewValue: 'Metabase' },
    { value: 'urlBi', viewValue: 'Url Web' },
    { value: 'params', viewValue: 'Url con parámetros' },
    { value: 'code', viewValue: 'Componentes' }
  ];

  // Texto literal para el hint del tipo 'params' (las llaves no se pueden escribir tal cual en la plantilla).
  readonly paramsPlaceholder = '{{uid}}';

  boardForm: FormGroup = this.fb.group({
    client_id: ['', [Validators.required]],
    name: ['', [Validators.required]],
    roles: this.fb.array([]),
    parameters: this.fb.array([]),
    urlBi: [''],
    type: ['urlBi', [Validators.required]],
    dashboard: [''],
    urlImage: [''],
    queries: this.fb.array([])
  });

  recognitionDetails$ = new Subject<any[]>();

  constructor(
    private readonly fb: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly boardService: DashboardsService,
    private readonly clientsService: ClientsService,
    private readonly router: Router,
    private readonly snackBar: MatSnackBar,
    private readonly bottomSheet: MatBottomSheet,
    private readonly cdr: ChangeDetectorRef,
    private readonly dialog: MatDialog,
    private readonly authService: AuthService,
    private readonly metabaseEmbedService: MetabaseEmbedService,

  ) {}

  ngOnInit(): void {
    // Carga clientes, usuarios y prepara el tablero actual.
    this.loadClients();
    this.setBoardMode();
    this.setupRoleStreams();
    this.setupMetabaseDashboardValidator();
    this.setupParamsUrlValidator();

    this.authService.userData$.subscribe((user) => {
      this.current_user_data = user;
      this.ensureCurrentUserRole();
    });

    this.boardService.loadUsers().subscribe((users) => {
      this.users = users;
      this.users_subject.next(users);

      this.ensureCurrentUserRole();
      this.initializeBoard();
    });

    this.boardForm.valueChanges.subscribe(() => {
      this.for_save = true;
    });
  }

  get rolesArray(): FormArray {
    // Acceso directo al FormArray de roles.
    return this.boardForm.get('roles') as FormArray;
  }

  get parametersArray(): FormArray {
    // Acceso directo al FormArray de parámetros por usuario.
    return this.boardForm.get('parameters') as FormArray;
  }

  get queriesArray(): FormArray {
    // Acceso directo al FormArray de consultas descargables.
    return this.boardForm.get('queries') as FormArray;
  }

  displayUserEmail(user: Users | string | null): string {
    if (!user) {
      return '';
    }
    return typeof user === 'string' ? user : user.email ?? '';
  }

  addRoleFromInput(): void {
    // Agrega un usuario al tablero desde el input de correo.
    const value = this.roleSearchCtrl.value;
    if (!value) {
      return;
    }
    this.addRole(value);
  }

  addRole(value: Users | string): void {
    // Busca el usuario y crea su acceso si todavía no existe.
    const user = typeof value === 'string'
      ? this.findUserByEmail(value)
      : value;

    if (!user?.uid || !user?.email) {
      this.snackBar.open('Selecciona un usuario con uid y correo validos.', 'cerrar');
      return;
    }

    if (this.getRoleIndexByUid(user.uid) === -1) {
      this.rolesArray.push(this.createRoleGroup({
        uid: user.uid,
        role: 'reader',
        email: user.email,
        name: user.name
      }));
      this.markFormChanged();
    }

    this.roleSearchCtrl.setValue('');
  }

  removeRole(uid: string): void {
    // Elimina el acceso del usuario y sus parámetros relacionados.
    const index = this.getRoleIndexByUid(uid);
    if (index >= 0) {
      this.rolesArray.removeAt(index);
      this.removeParametersForUid(uid);
      this.syncRoleRows();
      this.markFormChanged();
    }
  }

  addQuery(): void {
    // Agrega una nueva consulta descargable vacía.
    const queryGroup = this.fb.group({
      name: [''],
      host: [''],
      type: [''],
      active: true,
      params: this.fb.group({
        start_date: [null],
        end_date: [null]
      }, { validators: this.validateDateRange })
    });
    this.queriesArray.push(queryGroup);
  }

  removeQuery(index: number): void {
    // Elimina una consulta descargable del formulario.
    this.queriesArray.removeAt(index);
  }

  validateDateRange({ value: { start_date, end_date } }: AbstractControl): ValidationErrors | null {
    // Evita rangos de fecha invertidos.
    return start_date && end_date && new Date(start_date) > new Date(end_date)
      ? { invalidRange: true }
      : null;
  }

  createBoards(boardData: Partial<Dashboards>): void {
    // Crea un tablero nuevo.
    this.boardService.createDashboards(boardData).subscribe({
      next: (response) => {
        const dashboard_id = response?.dashboard_id;
        if (dashboard_id) {
          this.boardUid = dashboard_id;
          this.creating = false;
          this.router.navigate(['/dashboards', dashboard_id]);
        } else {
          this.router.navigate(['/dashboards']);
        }
        this.snackBar.open('Tablero creado.', 'ok');
      },
      error: () => this.snackBar.open('Error al crear el tablero.', 'cerrar')
    });
  }

  updateBoard(boardData: Partial<Dashboards>): void {
    // Actualiza solo la data base del tablero.
    this.boardService.updateDashboard(boardData, this.boardUid).subscribe({
      next: () => {
        this.router.navigate(['/dashboards']);
        this.snackBar.open('Tablero actualizado.', 'ok');
      },
      error: () => this.snackBar.open('Error al actualizar el tablero.', 'cerrar')
    });
  }

  saveBoard(): void {
    // Guarda el formulario básico del dashboard.
    if (!this.boardForm.valid) {
      this.boardForm.markAllAsTouched();
      return;
    }

    this.ensureCurrentUserRole();

    const { client_id, name, urlBi, urlImage, queries, type, dashboard } = this.boardForm.value;
    const roles = this.buildRolesPayload();
    const parameters = this.buildParametersPayload();
    const serializedQueries = (queries ?? []).map((query: any) => ({
      ...query,
      params: {
        start_date: query.params.start_date ? query.params.start_date.toISOString() : null,
        end_date: query.params.end_date ? query.params.end_date.toISOString() : null
      }
    }));

    if (type === 'params') {
      this.registerUrlTemplateParams(urlBi);
    }

    const boardData: Partial<Dashboards> = {
      client_id,
      name,
      urlBi,
      roles,
      parameters,
      parameters_objects: this.parameters_objects_subject.value,
      queries: serializedQueries,
      url_image: urlImage,
      type,
      dashboard: type === 'metabase' ? Number(dashboard) : undefined
    };

    this.default_boards = boardData;
    console.log('saveBoard boardData:', boardData);
    if (this.boardUid) {
      this.updateBoard(boardData);
    } else {
      this.createBoards(boardData);
    }

    this.for_save = false;
  }


  initializeBoard(): void {
    // Carga la información del tablero cuando existe uid.
    if (this.boardUid) {
      this.refreshBoard();
    }
  }

  openBottomSheet(): void {
    // Abre el bottom sheet de reconocimiento con la data actual.
    this.bottomSheet.open(SendRecognitionBottomsheetComponent, {
      data: this.boardData
    });
  }

  downloadCsv(): void {
    // Genera un CSV con los parámetros asignados a cada usuario.
    const data = this.roleRows;
    const realParameterKeys = Array.from(new Set(
      data.flatMap((user) => user.parameters.map((parameter) => parameter.key))
    ));
    const parameterKeys = realParameterKeys.filter((key) => key.trim().length > 0);
    const headers = ['usuario', ...parameterKeys.map((key) => `@param:${key}`)];

    const rows = data.map((user) => {
      const parameterMap = new Map(user.parameters.map((parameter) => [parameter.key, parameter.value]));
      return [
        this.escapeCsv(user.email),
        ...parameterKeys.map((key) => this.escapeCsv(parameterMap.get(key) ?? ''))
      ].join(',');
    });

    this.generateCsv(`${headers.join(',')}\n${rows.join('\n')}`);
  }

  generateCsv(csvContent: string): void {
    // Dispara la descarga del archivo CSV generado.
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'users.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  refreshBoard(): void {
    // Recarga el dashboard y reconstruye sus listas locales.
    this.boardService.getDashboardsByUid(this.boardUid).subscribe((board) => {
      this.default_boards = board;

      if (!board) {
        return;
      }

      this.creating = false;
      this.boardForm.patchValue({
        client_id: Number(board.client_id),
        name: board.name,
        urlBi: board.urlBi || '',
        urlImage: board.url_image,
        type: board.type || 'urlBi',
        dashboard: board.dashboard ?? ''
      });

      this.rolesArray.clear();
      this.parametersArray.clear();
      this.setParametersObjects(board.parameters_objects);

      if (board.roles && typeof board.roles === 'object') {
        Object.entries(board.roles).forEach(([uid, role]) => {
          const user = this.users.find((candidate) => candidate.uid === uid);
          if (!user) {
            return;
          }

          this.rolesArray.push(this.createRoleGroup({
            uid: user.uid,
            role: String(role),
            email: user.email,
            name: user.name
          }));

          const parameters = this.mapParameters(board.parameters?.[uid]);
          if (parameters.length > 0) {
            this.parametersArray.push(this.createParametersGroup({
              uid: user.uid,
              parameters
            }));
          }
        });
      }

      this.syncRoleRows();

      this.queriesArray.clear();
      if (Array.isArray(board.queries)) {
        board.queries.forEach((query: any) => {
          this.queriesArray.push(this.fb.group({
            name: [query.name || ''],
            host: [query.host || ''],
            type: [query.type],
            active: [query.active],
            params: this.fb.group({
              start_date: [this.parseDate(query.params?.start_date)],
              end_date: [this.parseDate(query.params?.end_date)]
            })
          }));
        });
      }

      this.boardData = board;
      this.setParametersObjects(board.parameters_objects);
      this.board_subject.next(this.default_boards);
      this.for_save = false;
      this.boardForm.markAsPristine();
      this.cdr.detectChanges();
    });
  }

  openParametersObjectsModal(): void {
    // Abre el modal para editar parameters_objects y guarda el resultado.

    const dialogRef = this.dialog.open(DashboardParameterObjectsModalComponent, {
      width: '920px',
      maxWidth: '96vw',
      panelClass: 'parameter-objects-modal-panel',
      data: {
        dashboard_name: this.boardData?.name || 'Dashboard',
        parameters_objects: this.parameters_objects_subject.value
      }
    });

    dialogRef.afterClosed().subscribe((result?: Record<string, DashboardParameterObjectItem>) => {
      if (!result) {
        return;
      }

      if (!this.boardUid) {
        this.setParametersObjects(result);
        return;
      }

      this.boardService.updateDashboard({ parameters_objects: result }, this.boardUid).subscribe({
        next: () => {
          this.setParametersObjects(result);
          this.snackBar.open('parameters_objects actualizados.', 'ok');
          this.boardService.loadDashboardsStore();
        },
        error: () => this.snackBar.open('No se pudieron guardar los parameters_objects.', 'cerrar')
      });
    });
  }

  parseDate(date: any): Date | null {
    // Convierte strings o timestamps a Date.
    if (!date) {
      return null;
    }
    if (date instanceof Date) {
      return date;
    }
    if (typeof date === 'string' && !isNaN(Date.parse(date))) {
      return new Date(date);
    }
    try {
      return new Date(Date.parse(date));
    } catch {
      console.warn(`No se pudo parsear la fecha: ${date}`);
      return null;
    }
  }

  loadClients(): void {
    // Carga la lista de clientes para el selector.
    this.clientsService.loadClients().subscribe((clients: any) => {
      this.clients = clients.data;
    });
  }

  openImportModal(): void {
    // Abre el modal para importar accesos del dashboard.
    if (!this.boardUid) {
      this.snackBar.open('Primero debes guardar el tablero para importar accesos.', 'cerrar');
      return;
    }

    const dialogRef = this.dialog.open(ImportFileUploadComponent, {
      width: '720px',
      maxWidth: '95vw',
      panelClass: 'import-upload-dialog'
    });
    dialogRef.componentInstance.entityLabel = 'accesos del dashboard';
    dialogRef.componentInstance.uploadHandler = (file: File) =>
      this.boardService.importDashboardAccess(this.boardUid, file);
    dialogRef.componentInstance.uploadSuccessHandler = () => {
      this.refreshBoard();
      this.snackBar.open('Accesos Actualizados.', 'ok');
    };
  }

  onDeleteItems(item: any): void {
    // Elimina un acceso desde la tabla.
    if (!item?.uid) {
      return;
    }
    this.removeRole(item.uid);
  }

  onEditItem(item: any): void {
    // Abre el diálogo para editar un acceso.
    const row = this.roleRows.find((role) => role.uid === item?.uid);
    if (!row) {
      return;
    }

    const dialogRef = this.dialog.open(DashboardRoleEditDialogComponent, {
      width: '760px',
      maxWidth: '95vw',
      data: {
        parameters_objects: this.parameters_objects$,
        role: {
          ...row,
          parameters: this.normalizeParameters(row.parameters)
        }
      }
    });

    dialogRef.afterClosed().subscribe((updatedRole?: DashboardRoleRow) => {
      if (!updatedRole) {
        return;
      }
      this.applyRoleEdition(updatedRole);
    });
  }

  private setBoardMode(): void {
    // Define si el componente está creando o editando.
    this.boardUid = this.route.snapshot.paramMap.get('uid') ?? '';
    this.creating = !this.boardUid;
    this.titleheader = this.creating ? 'Creando tablero' : 'Editando tablero';
  }

  private setupDynamicValidators(): void {
    // Ajusta reglas del formulario según el tipo de tablero.
    const typeControl = this.boardForm.get('type');
    const urlBiControl = this.boardForm.get('urlBi');

    if (!typeControl || !urlBiControl) {
      return;
    }

    const applyValidators = (type: string | null) => {
      if (type === 'urlBi') {
        urlBiControl.setValidators([Validators.required]);
      } else {
        urlBiControl.clearValidators();
      }
      urlBiControl.updateValueAndValidity({ emitEvent: false });
    };

    applyValidators(typeControl.value);
    typeControl.valueChanges.subscribe((type) => applyValidators(type));
  }

  private setupMetabaseDashboardValidator(): void {
    // El id de dashboard de Metabase solo es obligatorio para type === 'metabase'.
    const typeControl = this.boardForm.get('type');
    const dashboardControl = this.boardForm.get('dashboard');

    if (!typeControl || !dashboardControl) {
      return;
    }

    const applyValidators = (type: string | null) => {
      if (type === 'metabase') {
        dashboardControl.setValidators([Validators.required, Validators.min(1)]);
      } else {
        dashboardControl.clearValidators();
      }
      dashboardControl.updateValueAndValidity({ emitEvent: false });
    };

    applyValidators(typeControl.value);
    typeControl.valueChanges.subscribe((type) => applyValidators(type));
  }

  private setupParamsUrlValidator(): void {
    // En type === 'params' la URL es obligatoria y debe traer al menos un {{clave}},
    // si no, no hay donde poner el parámetro del usuario.
    const typeControl = this.boardForm.get('type');
    const urlBiControl = this.boardForm.get('urlBi');

    if (!typeControl || !urlBiControl) {
      return;
    }

    const requierePlaceholder = (control: AbstractControl): ValidationErrors | null =>
      getUrlTemplateKeys(control.value).length ? null : { missingPlaceholder: true };

    const applyValidators = (type: string | null) => {
      if (type === 'params') {
        urlBiControl.setValidators([Validators.required, requierePlaceholder]);
      } else {
        urlBiControl.clearValidators();
      }
      urlBiControl.updateValueAndValidity({ emitEvent: false });
    };

    applyValidators(typeControl.value);
    typeControl.valueChanges.subscribe((type) => applyValidators(type));
  }

  private registerUrlTemplateParams(urlBi: string): void {
    // Cada {{clave}} de la URL queda en parameters_objects: así aparece para
    // asignarla por usuario y el listado oculta el tablero a quien no la tenga.
    const actuales = this.parameters_objects_subject.value;
    const nuevos = getUrlTemplateKeys(urlBi).filter((clave) => !actuales[clave]);
    if (!nuevos.length) {
      return;
    }
    const agregados = nuevos.reduce((acc, clave) => {
      acc[clave] = { label: clave, type: 'string' };
      return acc;
    }, {} as Record<string, DashboardParameterObjectItem>);
    this.parameters_objects_subject.next({ ...actuales, ...agregados });
  }

  togglePreview(): void {
    if (this.showPreview) {
      this.showPreview = false;
      this.previewError = null;
      this.metabasePreviewUrl = null;
      return;
    }

    const type = this.boardForm.get('type')?.value;
    if (type === 'metabase') {
      const dashboardId = this.boardForm.get('dashboard')?.value;
      this.previewLoading = true;
      this.previewError = null;
      this.metabaseEmbedService.enbedUrl({}, Number(dashboardId)).subscribe({
        next: (response) => {
          this.metabasePreviewUrl = response?.url ?? null;
          this.previewLoading = false;
          this.showPreview = true;
        },
        error: () => {
          this.previewError = 'No se pudo cargar la previsualización. Verifica que el ID de Dashboard en Metabase sea correcto.';
          this.previewLoading = false;
          this.showPreview = true;
        }
      });
    } else {
      this.showPreview = true;
    }
  }

  onTypeSelectionChange(controlName: string, value: string): void {
    // selectionChange solo se dispara por interacción real del usuario, no por
    // patchValue (evita el aviso al abrir un tablero Metabase ya existente).
    if (controlName !== 'type') {
      return;
    }

    // Limpia el valor del campo que ya no aplica al tipo seleccionado, para no
    // dejar datos viejos guardados sin usar (URL de un tipo urlBi al pasar a
    // metabase, o el id de Metabase al pasar a urlBi/code).
    if (value !== 'urlBi' && value !== 'params') {
      this.boardForm.get('urlBi')?.setValue('');
    }
    if (value !== 'metabase') {
      this.boardForm.get('dashboard')?.setValue('');
    }

    if (value === 'metabase') {
      this.dialog.open(MetabaseSelectedDialogComponent, { width: '360px' });
    }
  }

  private setupRoleStreams(): void {
    const roles$ = this.rolesArray.valueChanges.pipe(
      startWith(this.rolesArray.getRawValue() as DashboardRoleFormValue[])
    );

    const parameters$ = this.parametersArray.valueChanges.pipe(
      startWith(this.parametersArray.getRawValue() as DashboardParametersByUser[])
    );

    this.filteredUsers = combineLatest([
      this.users$,
      this.roleSearchCtrl.valueChanges.pipe(startWith('')),
      roles$
    ]).pipe(
      map(([users, value, roles]) => this.filterUsers(users, value, roles))
    );

    this.roleRows$ = combineLatest([roles$, parameters$]).pipe(
      map(([roles, parameters]) => this.buildRoleRows(roles, parameters)),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.roleRows$.subscribe((rows) => {
      this.roleRows = rows;
    });

    this.roleEmailsSummary$ = this.roleRows$.pipe(
      map((rows) => rows.map((row) => row.email).filter((email) => !!email).join(', '))
    );
  }

  private filterUsers(users: Users[], value: Users | string | null, roles: DashboardRoleFormValue[]): Users[] {
    // Filtra usuarios por correo y excluye los ya agregados.
    const filterValue = this.getSearchTerm(value);
    const selectedUids = new Set(
      roles
        .map((role) => role.uid)
        .filter((uid) => !!uid)
    );

    return users.filter((user) =>
      !!user?.uid &&
      !!user?.email &&
      user.email.toLowerCase().includes(filterValue) &&
      !selectedUids.has(user.uid)
    );
  }

  private getSearchTerm(value: Users | string | null): string {
    if (!value) {
      return '';
    }
    return String(typeof value === 'string' ? value : value.email ?? '').trim().toLowerCase();
  }

  private findUserByEmail(email: string): Users | undefined {
    const normalizedEmail = String(email ?? '').trim().toLowerCase();
    return this.users.find((candidate) => candidate.email?.toLowerCase() === normalizedEmail);
  }

  private createRoleGroup(role: Partial<DashboardRoleFormValue>): FormGroup {
    // Crea el grupo base de un rol.
    return this.fb.group({
      uid: [role.uid ?? '', Validators.required],
      role: [role.role ?? 'reader', Validators.required],
      email: [role.email ?? '', [Validators.required, Validators.email]],
      name: [role.name ?? '']
    });
  }

  private createParametersGroup(entry: Partial<DashboardParametersByUser>): FormGroup {
    // Crea el grupo de parámetros por usuario.
    return this.fb.group({
      uid: [entry.uid ?? '', Validators.required],
      parameters: this.fb.array(
        this.normalizeParameters(entry.parameters).map((parameter) => this.createParameterGroup(parameter))
      )
    });
  }

  private createParameterGroup(parameter?: Partial<DashboardRoleParameter>): FormGroup {
    // Crea una fila de parámetro clave/valor.
    return this.fb.group({
      key: [parameter?.key ?? ''],
      value: [parameter?.value ?? '']
    });
  }

  private ensureCurrentUserRole(): void {
    // Garantiza el acceso del usuario actual al crear un tablero.
    if (!this.creating || !this.current_user_data?.uid) {
      return;
    }

    const existingIndex = this.getRoleIndexByUid(this.current_user_data.uid);
    if (existingIndex === -1) {
      this.rolesArray.push(this.createRoleGroup({
        uid: this.current_user_data.uid,
        role: 'reader',
        email: this.current_user_data.email ?? '',
        name: this.current_user_data.name ?? ''
      }));
      this.syncRoleRows();
      return;
    }

    this.rolesArray.at(existingIndex).patchValue({
      email: this.current_user_data.email ?? '',
      name: this.current_user_data.name ?? ''
    }, { emitEvent: false });
    this.syncRoleRows();
  }

  private getRoleIndexByUid(uid: string): number {
    // Busca un rol dentro del formulario.
    return this.rolesArray.controls.findIndex((control) => control.get('uid')?.value === uid);
  }

  private getParametersIndexByUid(uid: string): number {
    // Busca los parámetros de un usuario dentro del formulario.
    return this.parametersArray.controls.findIndex((control) => control.get('uid')?.value === uid);
  }

  private getParametersByUid(uid: string): DashboardRoleParameter[] {
    // Obtiene los parámetros de un usuario ya normalizados.
    const index = this.getParametersIndexByUid(uid);
    if (index === -1) {
      return [];
    }
    return this.normalizeParameters(this.parametersArray.at(index).getRawValue()?.parameters);
  }

  private updateParametersForUid(uid: string, parameters: DashboardRoleParameter[]): void {
    // Reemplaza los parámetros de un usuario existente.
    try {
      const normalized = this.normalizeParameters(parameters);
      const index = this.getParametersIndexByUid(uid);

      if (normalized.length === 0) {
        if (index >= 0) {
          this.parametersArray.removeAt(index);
        }
        return;
      }

      const group = this.createParametersGroup({ uid, parameters: normalized });
      if (index >= 0) {
        this.parametersArray.setControl(index, group);
        this.syncRoleRows();
        return;
      }

      this.parametersArray.push(group);
      this.syncRoleRows();
      this.snackBar.open(`Parámetros actualizados.`, 'ok');
    } catch (error) {
      console.error('Error al actualizar parámetros para UID:', uid, error);
      this.snackBar.open(`Error al actualizar parámetros.`, 'cerrar');
    }
  }

  private removeParametersForUid(uid: string): void {
    // Elimina los parámetros ligados a un usuario.
    try {
      const index = this.getParametersIndexByUid(uid);
      if (index >= 0) {
        this.parametersArray.removeAt(index);
        this.snackBar.open(`Parámetros eliminados para ${uid}.`, 'ok');
        this.syncRoleRows();
      }
    } catch (error) {
      console.error('Error al eliminar parámetros para UID:', uid, error);
      this.snackBar.open(`Error al eliminar parámetros para ${uid}.`, 'cerrar');
    }
  }

  private buildRolesPayload(): { [uid: string]: string } {
    // Serializa los roles para la API.
    return (this.rolesArray.getRawValue() as DashboardRoleFormValue[]).reduce((acc, role) => {
      acc[role.uid] = role.role;
      return acc;
    }, {} as { [uid: string]: string });
  }

  private buildParametersPayload(): { [uid: string]: { [key: string]: string } } | undefined {
    // Serializa los parámetros por usuario para guardar.
    const payload = (this.parametersArray.getRawValue() as DashboardParametersByUser[]).reduce((acc, entry) => {
      const normalized = this.normalizeParameters(entry.parameters);
      if (normalized.length > 0) {
        acc[entry.uid] = Object.fromEntries(normalized.map((parameter) => [parameter.key, parameter.value]));
      }
      return acc;
    }, {} as { [uid: string]: { [key: string]: string } });

    return Object.keys(payload).length > 0 ? payload : undefined;
  }

  private applyRoleEdition(updatedRole: DashboardRoleRow): void {
    // Guarda la edición de un acceso existente.
    const currentIndex = this.getRoleIndexByUid(updatedRole.uid);
    if (currentIndex === -1) {
      return;
    }

    const selectedUser = this.users.find((user) => user.email?.toLowerCase() === updatedRole.email.toLowerCase());
    if (!selectedUser) {
      this.snackBar.open('Selecciona un correo que exista en usuarios.', 'cerrar');
      return;
    }

    const duplicateIndex = this.getRoleIndexByUid(selectedUser.uid);
    if (duplicateIndex !== -1 && duplicateIndex !== currentIndex) {
      this.snackBar.open('Ese correo ya fue agregado al tablero.', 'cerrar');
      return;
    }

    const previousUid = updatedRole.uid;
    this.rolesArray.setControl(currentIndex, this.createRoleGroup({
      uid: selectedUser.uid,
      role: updatedRole.role,
      email: selectedUser.email,
      name: selectedUser.name
    }));

    if (previousUid !== selectedUser.uid) {
      this.removeParametersForUid(selectedUser.uid);
    }
    this.removeParametersForUid(previousUid);
    this.updateParametersForUid(selectedUser.uid, updatedRole.parameters);
    this.syncRoleRows();
    this.markFormChanged();
  }

  private mapParameters(parameters?: { [key: string]: string }): DashboardRoleParameter[] {
    // Convierte el objeto de parámetros guardado a lista editable.
    return Object.entries(parameters ?? {}).map(([key, value]) => ({
      key,
      value: String(value ?? '')
    }));
  }

  private normalizeParameters(parameters: unknown): DashboardRoleParameter[] {
    // Limpia los parámetros antes de guardarlos.
    if (!Array.isArray(parameters)) {
      return [];
    }

    return parameters
      .map((parameter) => ({
        key: String((parameter as DashboardRoleParameter)?.key ?? '').trim(),
        value: String((parameter as DashboardRoleParameter)?.value ?? '').trim()
      }))
      .filter((parameter) => parameter.key.length > 0);
  }

  private parseParameterObjects(parameters_objects: unknown): Record<string, DashboardParameterObjectItem> {
    if (!parameters_objects || typeof parameters_objects !== 'object' || Array.isArray(parameters_objects)) {
      return {};
    }

    return Object.entries(parameters_objects as Record<string, any>).reduce((acc, [key, value]) => {
      const label = String(value?.label ?? '').trim();
      const type = String(value?.type ?? 'string').trim() || 'string';

      if (label.length > 0) {
        acc[key] = { label, type };
      }

      return acc;
    }, {} as Record<string, DashboardParameterObjectItem>);
  }

  private setParametersObjects(parameters_objects: unknown): void {
    // Actualiza el estado reactivo de parameters_objects.
    this.parameters_objects_subject.next(this.parseParameterObjects(parameters_objects));
  }

  private buildParametersSummary(parameters_objects: Record<string, DashboardParameterObjectItem>): DashboardParameterSummaryVm {
    // Convierte el catálogo actual en chips listos para mostrar.
    const parameter_object_entries = Object.entries(parameters_objects)
      .map(([key, value]) => ({
        key,
        label: String(value?.label ?? '').trim()
      }))
      .filter((entry) => entry.label.length > 0);

    return {
      has_parameters: parameter_object_entries.length > 0,
      parameter_object_entries
    };
  }

  private markFormChanged(): void {
    // Marca el formulario como modificado.
    this.for_save = true;
    this.boardForm.markAsDirty();
    this.boardForm.updateValueAndValidity();
  }

  private syncRoleRows(): void {
    // Sincroniza la tabla visible con el FormArray de roles.
    const roles = this.rolesArray.getRawValue() as DashboardRoleFormValue[];
    const parameters = this.parametersArray.getRawValue() as DashboardParametersByUser[];
    this.roleRows = this.buildRoleRows(roles, parameters);
  }

  private buildRoleRows(
    roles: DashboardRoleFormValue[],
    parametersByUser: DashboardParametersByUser[]
  ): DashboardRoleRow[] {
    // Construye la tabla visible desde el estado actual del formulario.
    const parametersMap = new Map(
      parametersByUser.map((entry) => [entry.uid, this.normalizeParameters(entry.parameters)])
    );

    return roles.map((role) => ({
      ...role,
      parameters: parametersMap.get(role.uid) ?? []
    }));
  }

  private escapeCsv(value: string): string {
    // Escapa una celda antes de exportarla a CSV.
    return `"${String(value ?? '').replace(/"/g, '""')}"`;
  }

  
}
